import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const sections = [
  {
    n: "01",
    title: "Introduction",
    body: (
      <p>
        Freedom Coffee Inc. (&ldquo;FRDM Coffee,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your
        privacy and is committed to protecting your personal data. This Privacy
        Policy explains how we collect, use, disclose, and safeguard your
        information when you use our website, sign up for our mailing list, or
        otherwise interact with our services. Please read this policy carefully.
      </p>
    ),
  },
  {
    n: "02",
    title: "Information We Collect",
    body: (
      <div className="space-y-5">
        <div>
          <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-red mb-2">
            ◢ INFO YOU GIVE US
          </p>
          <ul className="space-y-2 list-none">
            <li className="flex gap-3">
              <span className="text-red font-mono">◢</span>
              <span>Your email address when you sign up for launch updates</span>
            </li>
            <li className="flex gap-3">
              <span className="text-red font-mono">◢</span>
              <span>Application info if you apply for a job (name, email, links you choose to share, optional resume)</span>
            </li>
            <li className="flex gap-3">
              <span className="text-red font-mono">◢</span>
              <span>Contact messages if you reach out via email</span>
            </li>
          </ul>
        </div>
        <div>
          <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-red mb-2">
            ◢ INFO COLLECTED AUTOMATICALLY
          </p>
          <ul className="space-y-2 list-none">
            <li className="flex gap-3">
              <span className="text-red font-mono">◢</span>
              <span>Device info — browser type, operating system, device identifiers</span>
            </li>
            <li className="flex gap-3">
              <span className="text-red font-mono">◢</span>
              <span>Usage data such as pages visited, time spent, and referring URLs</span>
            </li>
            <li className="flex gap-3">
              <span className="text-red font-mono">◢</span>
              <span>Approximate location derived from IP address</span>
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    n: "03",
    title: "How We Use It",
    body: (
      <ul className="space-y-2 list-none">
        {[
          "To tell you when we open the doors",
          "To respond to job applications and inquiries",
          "To improve the website and the experience",
          "To detect and prevent fraud or abuse",
          "To comply with legal obligations",
        ].map((it) => (
          <li key={it} className="flex gap-3">
            <span className="text-red font-mono">◢</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    ),
  },
  {
    n: "04",
    title: "Sharing",
    body: (
      <div className="space-y-4">
        <p>
          We do not sell your personal information. We may share it with
          service providers who help us run the business — email delivery,
          analytics, cloud hosting — and only as needed to do their jobs. We
          may also share it when required by law or in connection with a
          merger, acquisition, or sale of assets.
        </p>
      </div>
    ),
  },
  {
    n: "05",
    title: "Your Rights",
    body: (
      <div className="space-y-4">
        <p>
          Depending on where you live, you may have the right to access, correct,
          delete, or port your data, opt out of marketing, or withdraw consent.
          California residents have additional rights under the CCPA, including
          the right to opt out of any &ldquo;sale&rdquo; of personal information (we don&apos;t
          do that). To make a request, email{" "}
          <a
            href="mailto:privacy@frdm.coffee"
            className="text-red underline underline-offset-4 hover:text-bone"
          >
            privacy@frdm.coffee
          </a>
          .
        </p>
      </div>
    ),
  },
  {
    n: "06",
    title: "Security",
    body: (
      <p>
        We use reasonable technical and organizational measures to protect your
        data — encryption in transit, access controls, vendor due diligence. No
        method of transmission or storage is 100% secure, and we can&apos;t
        guarantee absolute security.
      </p>
    ),
  },
  {
    n: "07",
    title: "Cookies",
    body: (
      <p>
        We use cookies and similar technologies to remember preferences,
        understand how the site is used, and improve the experience. You can
        manage cookies through your browser settings; disabling them may affect
        functionality.
      </p>
    ),
  },
  {
    n: "08",
    title: "Children",
    body: (
      <p>
        Our site is not intended for children under 13, and we do not knowingly
        collect personal information from them. If you believe we have, contact
        us and we will delete it.
      </p>
    ),
  },
  {
    n: "09",
    title: "Changes",
    body: (
      <p>
        We may update this policy from time to time. We&apos;ll post the new version
        here and update the date below. Continued use of the site after changes
        means you accept the new policy.
      </p>
    ),
  },
  {
    n: "10",
    title: "Contact",
    body: (
      <div className="space-y-3">
        <p>If you have questions or want to exercise your rights:</p>
        <div className="border-4 border-bone p-5 font-mono uppercase tracking-[0.15em] text-xs md:text-sm space-y-1">
          <p className="text-red">◢ FREEDOM COFFEE INC.</p>
          <p>1850 POLK ST</p>
          <p>SAN FRANCISCO · CA</p>
          <p className="pt-2">
            <a
              href="mailto:privacy@frdm.coffee"
              className="text-bone hover:text-red"
            >
              privacy@frdm.coffee
            </a>
          </p>
        </div>
      </div>
    ),
  },
];

const PrivacyPolicy = () => {
  return (
    <div data-testid="page-privacy" className="min-h-screen bg-navy text-bone">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-navy text-bone border-b-2 border-red">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6 md:py-8 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-home-link"
            className="flex items-center gap-3 group"
            aria-label="Home"
          >
            <ArrowLeft
              size={18}
              className="text-bone/70 group-hover:text-red group-hover:-translate-x-0.5 transition-all"
            />
            <img
              src="/assets/frdm-logo.png"
              alt="FRDM"
              className="h-12 sm:h-14 md:h-20 lg:h-24 w-auto"
              style={{ filter: "invert(1) brightness(1.05)" }}
            />
          </Link>
          <span className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-bone/70">
            ◢ LEGAL
          </span>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-navy text-bone py-16 md:py-28 overflow-hidden">
        <div
          aria-hidden
          className="absolute -right-32 top-12 w-[700px] h-12 bg-red rotate-[28deg] hidden md:block"
        />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-12">
          <p className="font-mono uppercase tracking-[0.4em] text-[10px] md:text-xs text-red mb-6">
            ◢◢ LAST UPDATED · JANUARY 2026
          </p>
          <h1
            data-testid="privacy-headline"
            className="font-display uppercase leading-[0.82] tracking-tighter text-[14vw] md:text-[10vw] lg:text-[9vw]"
          >
            PRIVACY <span className="text-red">POLICY.</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-bone/80 max-w-2xl mt-8 leading-relaxed">
            We collect what we need. We don&apos;t sell anything. We respect your inbox.
          </p>
        </div>
      </section>

      {/* Sections */}
      <main className="bg-bone text-navy py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-5 md:px-12">
          <ul className="space-y-16 md:space-y-24">
            {sections.map((s) => (
              <li
                key={s.n}
                data-testid={`privacy-section-${s.n}`}
                className="grid grid-cols-12 gap-6 md:gap-8 border-t-4 border-navy pt-8 md:pt-10"
              >
                <div className="col-span-12 md:col-span-3">
                  <p className="font-display text-red leading-none text-[20vw] md:text-[8vw]">
                    {s.n}
                  </p>
                  <h2 className="font-display uppercase tracking-tight leading-none text-3xl md:text-4xl mt-3">
                    {s.title}
                  </h2>
                </div>
                <div className="col-span-12 md:col-span-9 font-body text-base md:text-lg leading-relaxed text-navy/85 space-y-4">
                  {s.body}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-navy text-bone border-t-2 border-bone/20">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <p className="font-mono uppercase tracking-[0.3em] text-[10px] text-bone/70">
            ◢ HQ · 1850 POLK ST · SAN FRANCISCO · CA
          </p>
          <div className="flex gap-5 font-mono uppercase tracking-[0.25em] text-[10px] text-bone/70">
            <Link to="/" className="hover:text-red">
              Home
            </Link>
            <Link to="/jobs" className="hover:text-red">
              Jobs
            </Link>
            <span>© 2026 FRDM</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
